/*
 * PREFERENCE STORE SAGAS
 *
 * These routines listen for request actions, and satisfies them.  These are asynchronous routines, and upon
 * completion will dispatch a new action with the results
 *
 *
 */

import type { SagaIterator } from 'redux-saga';
import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';

import { isDemoMode, requireEnvironmentConfig } from 'app-components/utils/core';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { Meta } from '../core/actionHelpers';
import * as actions from './preferencesActions';
import {
  mkPreferencesResponse,
  type PreferenceGroup,
  type PreferencePath,
  type PreferencesResponseProps,
  type SetPreferencePayload,
} from './preferencesTypes';

// GET_PREFERENCES, or the user/dataset action with its group filled in.
type GetPreferencesAction = { type: string; payload: PreferencePath & resourceStoreTypes.BaseResource; meta?: Meta };

// The update sends the payload's preference, not the payload.
type SetPreferenceResource = SetPreferencePayload & resourceStoreTypes.BaseResource;

const mkGetPreferencesConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<PreferencesResponseProps>();
const mkSetPreferenceConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<SetPreferenceResource>();

// User preferences are not dataset scoped.
const getAxiosConfig = (group: PreferenceGroup) =>
  group === 'dataset' ? undefined : { headers: { 'qcs-dataset-id': null } };

// ====================================================
// GET PREFERENCES
//
// The payload names the section and group: { section: 'shared', group: 'user' | 'dataset' }
//
export function* getPreferences(action: GetPreferencesAction): SagaIterator {
  const path = action.payload;
  try {
    const groupPath = path.group === 'dataset' ? '' : `/${path.group}`;
    const config = mkGetPreferencesConfig({
      resourceName: 'preferences',
      resourceBaseUrl: `${requireEnvironmentConfig().services_url}/preferences${groupPath}`,
      successAction: actions.getPreferencesSuccess,
      failureAction: actions.getPreferencesFailure,
      // QCS sends the group's preference document; keep the group it belongs to with it.
      transformResponseToResource: (response: { data: unknown }) =>
        mkPreferencesResponse({ path, data: response.data }),
      axiosConfig: getAxiosConfig(path.group),
    });
    yield call(resourceSagaUtils.qcsSyncGetResource<PreferencesResponseProps>, config, action);
  } catch (error) {
    // todo: make sure this is an axios error
    assert(false, error);

    yield put(actions.getPreferencesFailure(error));
  }
}

// ====================================================
// SET PREFERENCE
//
// Pass in an object { section, group, preference }
//
// preference is a object structure following the rules described here (do not send JSON, we convert for you)
// https://quickenteam.atlassian.net/wiki/spaces/EN/pages/99037174/Preference+Service
//
export function* setPreference(action: ReturnType<typeof actions.setPreference>): SagaIterator {
  const { group } = action.payload;
  const config = mkSetPreferenceConfig({
    resourceName: 'preferences',
    resourceBaseUrl: group === 'dataset' ? '/preferences' : `/preferences/${group}`,
    successAction: actions.setPreferenceSuccess,
    failureAction: actions.setPreferenceFailure,
    transformResourceToRequestData: (resource) => resource.preference,
    axiosConfig: getAxiosConfig(group),
  });
  yield call(resourceSagaUtils.qcsSyncUpdateResource<SetPreferenceResource>, config, action);
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

export function* getPreferencesActionWatcher(): SagaIterator {
  yield takeEvery(actions.getPreferences.type, getPreferences);
}

export function* getPreferencesUserActionWatcher(): SagaIterator {
  yield takeLeading(
    actions.getPreferencesUser.type,
    function* getPreferencesUser(action: ReturnType<typeof actions.getPreferencesUser>): SagaIterator {
      yield call(getPreferences, { ...action, payload: { section: 'shared', group: 'user' } });
    },
  );
}

export function* getPreferencesDatasetActionWatcher(): SagaIterator {
  yield takeLeading(
    actions.getPreferencesDataset.type,
    function* getPreferencesDataset(action: ReturnType<typeof actions.getPreferencesDataset>): SagaIterator {
      yield call(getPreferences, { ...action, payload: { section: 'shared', group: 'dataset' } });
    },
  );
}

export function* setPreferenceActionWatcher(): SagaIterator {
  if (!isDemoMode()) {
    yield takeEvery(actions.setPreference.type, setPreference);
  }
}

// ====================================================
// EXPORTS

export default [
  getPreferencesActionWatcher,
  getPreferencesDatasetActionWatcher,
  getPreferencesUserActionWatcher,
  setPreferenceActionWatcher,
];
